import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketsAPI } from '../../Api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  IconButton,
  useMediaQuery,
  Collapse
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  Payment as PaymentIcon,
  AccountBalanceWallet as WalletIcon,
  MoneyOff as NoWalletIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

const TicketDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width:600px)');
  const chatRef = useRef(null);
  const pollingInterval = useRef(null);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [flashNewMessage, setFlashNewMessage] = useState(false);
  
  // Dialogs
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [newLimit, setNewLimit] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const playNotificationSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZURE');
      audio.volume = 0.5;
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (err) {
      console.log('Notification sound error:', err);
    }
  };

  const fetchTicket = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const response = await ticketsAPI.getTicketById(id);
      const newTicket = response.data.data;
      
      // Проверяем новые сообщения от клиента
      if (ticket && newTicket.client) {
        const currentCount = newTicket.client.length;
        if (currentCount > lastMessageCount && lastMessageCount > 0) {
          playNotificationSound();
          
          // Запускаем визуальную анимацию
          setFlashNewMessage(true);
          setTimeout(() => setFlashNewMessage(false), 3000);
          
          // Показываем браузерное уведомление
          if ('Notification' in window && Notification.permission === 'granted') {
            new Notification('Новое сообщение от клиента', {
              body: newTicket.client[currentCount - 1]?.message?.substring(0, 100) || 'Новое сообщение',
              icon: '/favicon.ico',
              badge: '/favicon.ico'
            });
          }
        }
        setLastMessageCount(currentCount);
      } else if (newTicket.client) {
        setLastMessageCount(newTicket.client.length);
      }
      
      setTicket(newTicket);
      
      // Устанавливаем новый лимит по умолчанию
      if (newTicket.conversionStats) {
        setNewLimit(String(newTicket.conversionStats.currentLimit + 1));
      }
    } catch (err) {
      console.error('Error fetching ticket:', err);
      if (err.response?.status === 403) {
        setError('Доступ запрещен. Сначала возьмите тикет в работу.');
      } else {
        setError(err.response?.data?.message || 'Ошибка загрузки тикета');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
    
    // Запрашиваем разрешение на уведомления
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    // Запускаем polling каждые 5 секунд для отслеживания новых сообщений
    pollingInterval.current = setInterval(() => {
      fetchTicket(true); // silent = true, чтобы не показывать загрузку
    }, 5000);
    
    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [id]);

  const handleReply = async () => {
    if (!replyMessage.trim()) return;
    
    try {
      setSending(true);
      await ticketsAPI.replyToTicket(id, replyMessage.trim());
      setReplyMessage('');
      await fetchTicket();
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка отправки ответа');
    } finally {
      setSending(false);
    }
  };

  const handleApprove = async () => {
    try {
      setSending(true);
      await ticketsAPI.approveTicket(id, parseInt(newLimit));
      setApproveDialog(false);
      await fetchTicket();
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка одобрения тикета');
    } finally {
      setSending(false);
    }
  };

  const handleReject = async () => {
    try {
      setSending(true);
      await ticketsAPI.rejectTicket(id, rejectReason);
      setRejectDialog(false);
      await fetchTicket();
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка отклонения тикета');
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!window.confirm('Закрыть тикет?')) return;
    
    try {
      setSending(true);
      await ticketsAPI.closeTicket(id);
      await fetchTicket();
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка закрытия тикета');
    } finally {
      setSending(false);
    }
  };

  const handleMarkPaid = async (addBalance) => {
    try {
      setSending(true);
      await ticketsAPI.markOrderAsPaid(id, addBalance);
      setPaymentDialog(false);
      await fetchTicket();
    } catch (err) {
      alert(err.response?.data?.message || 'Ошибка подтверждения оплаты');
    } finally {
      setSending(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      created: 'info',
      in_progress: 'warning',
      closed: 'default',
      approved: 'success',
      rejected: 'error'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      created: 'Создан',
      in_progress: 'В работе',
      closed: 'Закрыт',
      approved: 'Одобрен',
      rejected: 'Отклонен'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !ticket) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Тикет не найден'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/tickets')}>
          Назад к списку
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 }, pb: { xs: 10, sm: 3 } }}>
      <IconButton
        onClick={() => navigate('/tickets')}
        sx={{ mb: 1 }}
      >
        <ArrowBackIcon />
      </IconButton>

      {/* Основная информация */}
      <Card sx={{ mb: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 600, mb: 0.5 }}>
                {ticket.type === 'limit_increase' ? '📈' : '💳'} #{ticket._id.slice(-6)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {ticket.type === 'limit_increase' ? 'Увеличение лимита' : 'Проблема с оплатой'}
              </Typography>
            </Box>
            <Chip
              label={getStatusLabel(ticket.status)}
              color={getStatusColor(ticket.status)}
              size={isMobile ? 'small' : 'medium'}
            />
          </Box>

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {ticket.tg_id?.userId || '—'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Создан: {new Date(ticket.created_at).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>

            {ticket.type === 'payment_issue' && ticket.order && (
              <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  📄 Информация о заказе
                </Typography>
                <Stack spacing={0.75}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Заказ:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {ticket.order.order_id.slice(0, 12)}...
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          navigator.clipboard.writeText(ticket.order.order_id);
                          alert('Номер заказа скопирован!');
                        }}
                        sx={{ minWidth: 'auto', p: 0.25, fontSize: '0.65rem' }}
                      >
                        📋
                      </Button>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Мерчант:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                      {ticket.merchant?.name || '—'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Проц:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                      {ticket.order.platform || 'Yandex'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Статус:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, color: ticket.order.status === 'PAID' ? '#4caf50' : '#f44336' }}>
                      {ticket.order.status}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                      Сумма:
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                      {Number(ticket.order.total_amount || 0)}₽ / {Number(ticket.order.amount || 0)}₽
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            )}

            {ticket.conversionStats && (
              <Box sx={{ mt: 1.5 }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setShowStats(!showStats)}
                  endIcon={showStats ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ justifyContent: 'space-between', textTransform: 'none' }}
                >
                  📊 Статистика пользователя
                </Button>
                <Collapse in={showStats}>
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Лимит
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ticket.conversionStats.currentLimit}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Конверсия
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ticket.conversionStats.conversionRate}%
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Оплачено
                        </Typography>
                        <Typography variant="body2">
                          {ticket.conversionStats.paidOrders}/{ticket.conversionStats.totalOrders}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Средний чек
                        </Typography>
                        <Typography variant="body2">
                          {ticket.conversionStats.avgOrderAmount}₽
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Box>
            )}
          </Stack>

          {/* Действия - мобильная версия */}
          {isMobile && ticket.status !== 'closed' && (
            <Box sx={{ 
              position: 'fixed', 
              bottom: 0, 
              left: 0, 
              right: 0, 
              p: 1.5, 
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              zIndex: 1000,
              display: 'flex',
              gap: 1
            }}>
              {ticket.type === 'limit_increase' && ticket.status !== 'approved' && ticket.status !== 'rejected' && (
                <>
                  <Button
                    fullWidth
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => setApproveDialog(true)}
                    disabled={sending}
                  >
                    ✅ Одобрить
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => setRejectDialog(true)}
                    disabled={sending}
                  >
                    ❌ Отклонить
                  </Button>
                </>
              )}
              {ticket.type === 'payment_issue' && ticket.order && ticket.order.status !== 'PAID' && (
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  size="small"
                  onClick={() => setPaymentDialog(true)}
                  disabled={sending}
                >
                  💰 Подтвердить
                </Button>
              )}
              {ticket.status !== 'closed' && (
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  onClick={handleClose}
                  disabled={sending}
                >
                  🔒 Закрыть
                </Button>
              )}
            </Box>
          )}

          {/* Действия - десктоп версия */}
          {!isMobile && (
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {ticket.type === 'limit_increase' && ticket.status !== 'approved' && ticket.status !== 'rejected' && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setApproveDialog(true)}
                    disabled={sending}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    Одобрить
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => setRejectDialog(true)}
                    disabled={sending}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    Отклонить
                  </Button>
                </>
              )}

              {ticket.type === 'payment_issue' && ticket.order && ticket.order.status !== 'PAID' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PaymentIcon />}
                  onClick={() => setPaymentDialog(true)}
                  disabled={sending}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Подтвердить оплату
                </Button>
              )}

              {ticket.status !== 'closed' && (
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={handleClose}
                  disabled={sending}
                  size={isMobile ? 'small' : 'medium'}
                >
                  Закрыть тикет
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* История переписки */}
      <Card sx={{ 
        mb: 2, 
        boxShadow: 2,
        border: flashNewMessage ? '2px solid #4caf50' : 'none',
        transition: 'all 0.3s ease',
        animation: flashNewMessage ? 'glow 1s ease-in-out 3' : 'none',
        '@keyframes glow': {
          '0%, 100%': { boxShadow: '0 0 5px rgba(76, 175, 80, 0.5)' },
          '50%': { boxShadow: '0 0 20px rgba(76, 175, 80, 0.8)' }
        }
      }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
          <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
            💬 История переписки
            {flashNewMessage && (
              <Chip 
                label="Новое!" 
                size="small" 
                color="success" 
                sx={{ 
                  ml: 1, 
                  height: 20, 
                  fontSize: '0.65rem',
                  animation: 'pulse 1s ease-in-out 3',
                  '@keyframes pulse': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 1 },
                    '50%': { transform: 'scale(1.1)', opacity: 0.8 }
                  }
                }} 
              />
            )}
          </Typography>

          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1.5,
            maxHeight: '500px',
            overflowY: 'auto',
            p: 1,
            bgcolor: '#f5f5f5',
            borderRadius: 2
          }}>
            {/* Объединяем и сортируем сообщения по времени */}
            {(() => {
              const allMessages = [
                ...(ticket.client || []).map(msg => ({ ...msg, type: 'client', date: msg.created || msg.date })),
                ...(ticket.operator || []).map(msg => ({ ...msg, type: 'operator', date: msg.created || msg.date }))
              ].sort((a, b) => new Date(a.date) - new Date(b.date));

              return allMessages.map((msg, idx) => (
                <Box 
                  key={`${msg.type}-${idx}`}
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%'
                  }}
                >
                  <Paper 
                    elevation={1}
                    sx={{ 
                      p: { xs: 1.2, sm: 1.5 }, 
                      maxWidth: { xs: '85%', sm: '70%' },
                      bgcolor: msg.type === 'client' ? '#ffffff' : '#667eea',
                      color: msg.type === 'client' ? 'text.primary' : '#ffffff',
                      borderRadius: '4px 16px 16px 16px',
                      boxShadow: msg.type === 'client' 
                        ? '0 1px 2px rgba(0,0,0,0.1)' 
                        : '0 2px 8px rgba(102, 126, 234, 0.3)',
                      position: 'relative'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        opacity: 0.7,
                        display: 'block',
                        mb: 0.5,
                        fontWeight: 500
                      }}
                    >
                      {msg.type === 'client' ? '👤 Клиент' : '🛠️ Оператор'}
                    </Typography>
                    <Typography 
                      variant={isMobile ? 'body2' : 'body1'} 
                      sx={{ 
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5
                      }}
                    >
                      {msg.message}
                    </Typography>
                    {msg.attachments?.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: msg.type === 'client' ? '1px solid #e0e0e0' : '1px solid rgba(255,255,255,0.2)' }}>
                        {msg.attachments.map((att, i) => (
                          <Typography 
                            key={i} 
                            variant="caption" 
                            display="block" 
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              opacity: 0.9
                            }}
                          >
                            📎 <a 
                              href={att} 
                              target="_blank" 
                              rel="noopener noreferrer" 
                              style={{ 
                                color: msg.type === 'client' ? '#667eea' : '#ffffff',
                                textDecoration: 'underline',
                                wordBreak: 'break-all'
                              }}
                            >
                              {att.substring(att.lastIndexOf('/') + 1, att.lastIndexOf('/') + 30)}...
                            </a>
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontSize: { xs: '0.6rem', sm: '0.65rem' },
                        opacity: 0.6,
                        display: 'block',
                        mt: 0.5,
                        textAlign: 'right'
                      }}
                    >
                      {new Date(msg.date).toLocaleString('ru-RU', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </Typography>
                  </Paper>
                </Box>
              ));
            })()}
          </Box>

          {/* Форма ответа */}
          {ticket.status !== 'closed' && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={isMobile ? 3 : 4}
                size={isMobile ? 'small' : 'medium'}
                label="Ваш ответ"
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                disabled={sending}
              />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleReply}
                disabled={!replyMessage.trim() || sending}
                size={isMobile ? 'small' : 'medium'}
                sx={{ mt: 1.5 }}
              >
                Отправить
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Чеки и видео */}
      {(ticket.receipt_urls?.length > 0 || ticket.video_urls?.length > 0) && (
        <Card sx={{ mb: 2, boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} gutterBottom sx={{ fontWeight: 600 }}>
              📎 Прикрепленные файлы
            </Typography>
            
            {ticket.receipt_urls?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  🧾 Чеки ({ticket.receipt_urls.length})
                </Typography>
                <Stack spacing={1}>
                  {ticket.receipt_urls.map((url, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        p: 1, 
                        bgcolor: '#f5f5f5', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        📄 Чек {idx + 1}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text"
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          alert('Ссылка скопирована!');
                        }}
                        sx={{ minWidth: 'auto', px: 1, fontSize: '0.65rem' }}
                      >
                        📋
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        href={url} 
                        target="_blank"
                        sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.7rem' }}
                      >
                        Открыть
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            
            {ticket.video_urls?.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  🎥 Видео ({ticket.video_urls.length})
                </Typography>
                <Stack spacing={1}>
                  {ticket.video_urls.map((url, idx) => (
                    <Box 
                      key={idx} 
                      sx={{ 
                        p: 1, 
                        bgcolor: '#fff3e0', 
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        🎬 Видео {idx + 1}
                      </Typography>
                      <Button 
                        size="small" 
                        variant="text"
                        onClick={() => {
                          navigator.clipboard.writeText(url);
                          alert('Ссылка скопирована!');
                        }}
                        sx={{ minWidth: 'auto', px: 1, fontSize: '0.65rem' }}
                      >
                        📋
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined"
                        color="warning"
                        href={url} 
                        target="_blank"
                        sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.7rem' }}
                      >
                        Смотреть
                      </Button>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Логи ордера */}
      {ticket.order?.logs?.length > 0 && (
        <Card sx={{ boxShadow: 2 }}>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            <Button
              fullWidth
              size="small"
              onClick={() => setShowLogs(!showLogs)}
              endIcon={showLogs ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{ justifyContent: 'space-between', textTransform: 'none', mb: showLogs ? 1 : 0 }}
            >
              📋 История ордера ({ticket.order.logs.length})
            </Button>
            <Collapse in={showLogs}>
              <Stack spacing={0.5}>
                {ticket.order.logs.slice(-10).map((log, idx) => (
                  <Typography key={idx} variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                    {idx + 1}. [{new Date(log.date).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}] {log.text}
                  </Typography>
                ))}
              </Stack>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Диалог одобрения */}
      <Dialog open={approveDialog} onClose={() => setApproveDialog(false)}>
        <DialogTitle>Одобрить запрос на увеличение лимита</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            type="number"
            label="Новый лимит"
            value={newLimit}
            onChange={(e) => setNewLimit(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(false)}>Отмена</Button>
          <Button onClick={handleApprove} variant="contained" color="success" disabled={!newLimit || sending}>
            Одобрить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог отклонения */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} fullScreen={isMobile}>
        <DialogTitle>Отклонить запрос</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={3}
            size={isMobile ? 'small' : 'medium'}
            label="Причина отклонения (опционально)"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} size={isMobile ? 'small' : 'medium'}>Отмена</Button>
          <Button onClick={handleReject} variant="contained" color="error" disabled={sending} size={isMobile ? 'small' : 'medium'}>
            Отклонить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения оплаты */}
      <Dialog open={paymentDialog} onClose={() => setPaymentDialog(false)} fullScreen={isMobile} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Подтвердить оплату</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Выберите действие:
          </Typography>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={() => handleMarkPaid(false)}
              disabled={sending}
              sx={{ 
                justifyContent: 'flex-start',
                p: 2,
                textAlign: 'left',
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <NoWalletIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Без пополнения баланса
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Только изменить статус на PAID
                  </Typography>
                </Box>
              </Box>
            </Button>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => handleMarkPaid(true)}
              disabled={sending}
              sx={{ 
                justifyContent: 'flex-start',
                p: 2,
                textAlign: 'left'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <WalletIcon sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    С пополнением баланса
                  </Typography>
                  <Typography variant="caption">
                    PAID + пополнить на {Number(ticket.order?.amount || ticket.order?.total_amount || 0)}₽
                  </Typography>
                </Box>
              </Box>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialog(false)} disabled={sending} size={isMobile ? 'small' : 'medium'}>
            Отмена
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketDetail;
